import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Upload } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import { api } from '../lib/api';
import { CATEGORIES } from '../lib/social';

const CATEGORY_LABELS = {
  aura_moment: 'Aura Moment', sports: 'Deportes', style: 'Estilo', creativity: 'Creatividad',
  social: 'Social', humor: 'Humor', dance: 'Baile', music: 'Música',
  achievement: 'Logro', positive_action: 'Acción positiva', family: 'Familia',
  school: 'Escuela', community: 'Comunidad', gaming: 'Gaming', other: 'Otro',
};

/**
 * Unified Create screen. `variant` decides the UI:
 *   'text' | 'photo' | 'video' | 'aura_moment' | 'positive'
 */
export default function CreatePostPage({ variant = 'text' }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState('');
  const [textBody, setTextBody] = useState('');
  const [category, setCategory] = useState(variant === 'aura_moment' ? 'aura_moment' : 'other');
  const [privacy, setPrivacy] = useState('public');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const needsMedia = variant === 'photo' || variant === 'aura_moment';

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Reject videos — feature disabled platform-wide (Feb 2026)
    if (f.type.startsWith('video/')) {
      toast.error(t('feed.videosDisabled') || 'La subida de videos está deshabilitada');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  if (variant === 'positive') {
    // Community Aura requires witnesses, not implemented yet — inform user
    return (
      <div className="min-h-screen bg-aura-bg px-5 pt-8 pb-24">
        <button onClick={() => nav(-1)} className="text-aura-textDim mb-6 inline-flex items-center gap-1"><ChevronLeft size={18} />{t('common.back')}</button>
        <h1 className="font-display font-black text-3xl aura-text-gradient mb-3">{t('feed.createPositive')}</h1>
        <p className="text-white/85 text-sm max-w-md">{t('feed.positiveNotice')}</p>
        <span className="mt-4 inline-block aura-chip">{t('common.comingSoon')}</span>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (needsMedia && !file) { toast.error(t('feed.pickFile')); return; }
    if (variant === 'text' && !textBody.trim()) { toast.error(t('common.error')); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('type', variant === 'aura_moment' ? 'aura_moment' : variant);
      if (caption) fd.append('caption', caption);
      if (variant === 'text' && textBody) fd.append('text_body', textBody);
      if (category) fd.append('category', category);
      fd.append('privacy', privacy);
      if (file) fd.append('file', file);
      const { data } = await api.post('/posts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('feed.published'));
      nav(`/post/${data.public_post_id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally { setBusy(false); }
  };

  const titleKey = {
    text: 'feed.createText', photo: 'feed.createPhoto',
    video: 'feed.createVideo', aura_moment: 'feed.createAuraMoment',
  }[variant];

  const accept = variant === 'photo' ? 'image/*' : 'image/*';

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim hover:text-white flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} /> {t('common.back')}
        </button>
        <button disabled={busy} onClick={submit} data-testid="create-publish" className="aura-btn-primary !py-2 !px-4 !text-sm disabled:opacity-60">
          {busy ? t('feed.publishing') : t('common.publish')}
        </button>
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4">
        <h1 className="font-display font-black text-2xl aura-text-gradient mb-4">{t(titleKey)}</h1>

        {needsMedia && (
          <section className="aura-card p-4 mb-4">
            {preview ? (
              <img src={preview} alt="preview" className="w-full max-h-[50vh] rounded-lg object-contain bg-black" />
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="btn-pick-file" className="w-full aura-btn-ghost h-14 border-dashed">
                <Upload size={18} /> {t('feed.picture')}
              </button>
            )}
            <input ref={fileRef} type="file" accept={accept} hidden onChange={onPickFile} data-testid="file-input" />
            {preview && (
              <button type="button" onClick={() => fileRef.current?.click()} className="mt-3 text-xs text-aura-cyan">{t('feed.pickFile')}</button>
            )}
          </section>
        )}

        {variant === 'text' ? (
          <textarea
            className="aura-input min-h-[180px] py-3"
            placeholder={t('feed.textPlaceholder')}
            value={textBody}
            onChange={(e) => setTextBody(e.target.value)}
            maxLength={500}
            data-testid="text-body"
          />
        ) : (
          <textarea
            className="aura-input min-h-[80px] py-3"
            placeholder={t('feed.captionPlaceholder')}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={500}
            data-testid="caption"
          />
        )}

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">{t('feed.category')}</div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                data-testid={`cat-${c}`}
                className={`aura-chip !text-[11px] ${category === c ? 'border-aura-cyan text-aura-cyan' : ''}`}
              >
                {CATEGORY_LABELS[c] || c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">{t('feed.privacy')}</div>
          <div className="flex gap-2">
            {['public', 'followers', 'self'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPrivacy(p)}
                data-testid={`privacy-${p}`}
                className={`flex-1 rounded-xl border py-2 text-sm font-display font-semibold ${privacy === p ? 'border-aura-cyan text-aura-cyan' : 'border-white/10 text-white/80'}`}
              >
                {t(`feed.privacy${p === 'public' ? 'Public' : p === 'followers' ? 'Followers' : 'Self'}`)}
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
